'use client';

/**
 * SudoChallengeModal — Step-up authentication / Sudo Mode (M4).
 *
 * Renders a modal prompting the user to re-authenticate before a
 * sensitive action proceeds. The Go API gates routes like
 * `DELETE /api/projects/{id}` on a fresh sudo grant; on miss it
 * returns 412 with `code: "sudo_mode_required"`. The companion
 * `useSudoMode` hook dispatches a window event in response, which
 * this component picks up and surfaces to the user.
 *
 * Design notes:
 *
 *   - Priority order (most secure first): Passkey UV → TOTP → Password.
 *     The hook reads `methods` from `/api/auth/sudo/challenge` so we
 *     never offer a method the user hasn't enrolled.
 *   - On lockout (sudo_mode_locked) we render a Retry-After cooldown
 *     view and disable the submit button until the timer elapses.
 *   - On success the hook dispatches `zenopus:sudo-granted` so any
 *     pending retry loop (`runWithSudoMode`) can re-enter cleanly.
 *
 * Wire once at the authenticated app shell (e.g. in a top-level
 * AppLayout). The component is a no-op until `useSudoMode().pending`
 * becomes non-null.
 */

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Fingerprint, KeyRound, Lock, ShieldCheck } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { usePasskey } from '@/hooks/auth/usePasskey';
import { useSudoMode, type SudoActionClass, type SudoMethod } from '@/hooks/auth/useSudoMode';

/**
 * Maps action class to the i18n key under `auth.sudoMode.actionClass.*`
 * so the modal title can describe the action concretely. We default
 * to a generic key when the class is unrecognised (e.g. a future
 * action added server-side before the client is updated) so we
 * never render a raw enum value.
 */
const ACTION_CLASS_LABEL: Record<SudoActionClass, string> = {
  billing: 'auth.sudoMode.actionClass.billing',
  project_delete: 'auth.sudoMode.actionClass.projectDelete',
  workspace_delete: 'auth.sudoMode.actionClass.workspaceDelete',
  api_token_create: 'auth.sudoMode.actionClass.apiTokenCreate',
  account_delete: 'auth.sudoMode.actionClass.accountDelete',
  email_change: 'auth.sudoMode.actionClass.emailChange',
  admin_revoke: 'auth.sudoMode.actionClass.adminRevoke',
};

/**
 * Maps a sudo method to the i18n label key under
 * `auth.sudoMode.methods.*`. Same pattern as ACTION_CLASS_LABEL.
 */
const METHOD_LABEL: Record<SudoMethod, string> = {
  password: 'auth.sudoMode.methods.password',
  totp: 'auth.sudoMode.methods.totp',
  'passkey-uv': 'auth.sudoMode.methods.passkey',
};

const METHOD_ICON: Record<SudoMethod, typeof Fingerprint> = {
  'passkey-uv': Fingerprint,
  totp: KeyRound,
  password: Lock,
};

export function SudoChallengeModal() {
  const t = useTranslations();
  const sudo = useSudoMode();
  const passkey = usePasskey();

  // Track which method is currently selected. We default to the
  // most secure method available so the priority order is implicit
  // in the UI even before the user changes tabs.
  const [activeMethod, setActiveMethod] = useState<SudoMethod | null>(null);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Auto-select the highest-priority available method when the modal
  // opens. We re-run when `methods` changes so a refresh of the list
  // (e.g. user enrolls a passkey in another tab) updates the default.
  // setTimeout(0) defers the setState out of the synchronous effect
  // body so React 19's react-hooks/set-state-in-effect lint passes.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (!sudo.methods) {
        setActiveMethod(null);
        return;
      }
      if (sudo.methods.includes('passkey-uv')) {
        setActiveMethod('passkey-uv');
      } else if (sudo.methods.includes('totp')) {
        setActiveMethod('totp');
      } else if (sudo.methods.includes('password')) {
        setActiveMethod('password');
      } else {
        setActiveMethod(null);
      }
    }, 0);
    return () => clearTimeout(handle);
  }, [sudo.methods]);

  // Lockout countdown — drive the visible counter from a state value
  // updated inside an effect. This keeps the render path free of
  // impure-function and ref-read warnings (React 19's purity rules).
  const open = Boolean(sudo.pending);
  const pending = sudo.pending;
  const [remainingLockSeconds, setRemainingLockSeconds] = useState(0);

  useEffect(() => {
    if (!pending?.locked) {
      // Reset is deferred via setTimeout(0) so React 19's
      // react-hooks/set-state-in-effect rule passes — same trick used
      // in components/settings/DeployTargetsSettings.tsx.
      const cleanup = setTimeout(() => setRemainingLockSeconds(0), 0);
      return () => clearTimeout(cleanup);
    }
    const startedAt = performance.now();
    const recompute = () => {
      const elapsed = Math.floor((performance.now() - startedAt) / 1000);
      setRemainingLockSeconds(Math.max(0, pending.retryAfterSeconds - elapsed));
    };
    // setTimeout(0) defers the initial recompute out of the synchronous
    // effect body for the same reason as above.
    const initial = setTimeout(recompute, 0);
    const id = window.setInterval(recompute, 1000);
    return () => {
      clearTimeout(initial);
      window.clearInterval(id);
    };
  }, [pending?.locked, pending?.retryAfterSeconds]);

  if (!open || !pending) return null;

  const actionLabelKey = ACTION_CLASS_LABEL[pending.actionClass] ?? 'auth.sudoMode.actionClass.generic';

  // The lockout is only *effective* while the cooldown counter is
  // still running. `pending.locked` stays true for the lifetime of the
  // pending challenge (the hook never clears it), so gating the form
  // purely on it leaves inputs and the submit button permanently
  // disabled after the countdown reaches 0 — the user would have to
  // Cancel (losing the pending action) and redo. Deriving the active
  // lock state from the live counter re-enables the form the instant
  // the cooldown elapses.
  const isLocked = pending.locked && remainingLockSeconds > 0;

  const handleClose = () => {
    setPassword('');
    setTotpCode('');
    setLocalError(null);
    setSubmitting(false);
    sudo.close();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeMethod || submitting) return;
    setLocalError(null);
    setSubmitting(true);
    try {
      if (activeMethod === 'passkey-uv') {
        if (!passkey.isSupported) {
          setLocalError(t('auth.sudoMode.errors.passkeyNotSupported'));
          return;
        }
        let header: string;
        try {
          header = await passkey.createUvAssertion('sudo_mode');
        } catch (err) {
          setLocalError(t('auth.sudoMode.errors.passkeyFailed'));
          return;
        }
        await sudo.verify({
          method: 'passkey-uv',
          actionClass: pending.actionClass,
          credentials: {},
          passkeyAssertionHeader: header,
        });
      } else if (activeMethod === 'totp') {
        const code = totpCode.trim();
        if (!code) {
          setLocalError(t('auth.sudoMode.errors.invalidTotp'));
          return;
        }
        await sudo.verify({
          method: 'totp',
          actionClass: pending.actionClass,
          credentials: { totpCode: code },
        });
      } else if (activeMethod === 'password') {
        if (!password) {
          setLocalError(t('auth.sudoMode.errors.invalidCredentials'));
          return;
        }
        await sudo.verify({
          method: 'password',
          actionClass: pending.actionClass,
          credentials: { password },
        });
      }
    } catch {
      // Hook already populated `sudo.error`. No-op here so the form
      // stays in the modal for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = localError ?? (sudo.error ? t(sudo.error) : null);

  // The verification form (which owns the in-form Cancel button) renders
  // only once a method is selected from a non-empty method list. While
  // methods load or when the account has no enrolled method, we still need
  // a visible, discoverable way out of the modal — otherwise only
  // Esc/backdrop-click dismiss it, which is an a11y gap for a dialog
  // rendered with `hideClose`.
  const hasMethods = Boolean(sudo.methods && sudo.methods.length > 0);
  // Standalone dismiss control for the loading + no-methods states. We gate
  // it on `!hasMethods` (rather than `!showForm`) so it does not flash in the
  // sub-frame between methods arriving and `activeMethod` being auto-selected
  // — which would swap the button out from under an in-flight click.
  const showStandaloneCancel = !hasMethods;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="max-w-md" hideClose>
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-zen-muted text-zen-text">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-zen-text">{t('auth.sudoMode.title')}</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-zen-text-muted">
                {t('auth.sudoMode.description', { action: t(actionLabelKey) })}
              </DialogDescription>
            </div>
          </div>

          {isLocked ? (
            <div className="rounded-control border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-zen-text">
              <p className="font-medium">{t('auth.sudoMode.locked.title')}</p>
              <p className="mt-1 text-xs text-zen-text-muted">
                {t('auth.sudoMode.locked.description', { seconds: remainingLockSeconds })}
              </p>
            </div>
          ) : null}

          {sudo.methodsLoading ? (
            <p className="text-sm text-zen-text-muted">{t('auth.sudoMode.loadingMethods')}</p>
          ) : null}

          {sudo.methods && sudo.methods.length === 0 ? (
            <div className="rounded-control border border-zen-border bg-zen-surface p-3 text-sm text-zen-text-muted">
              {t('auth.sudoMode.errors.noMethodAvailable')}
            </div>
          ) : null}

          {showStandaloneCancel ? (
            <div className="flex justify-end pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                {t('auth.sudoMode.cancel')}
              </Button>
            </div>
          ) : null}

          {activeMethod && sudo.methods && sudo.methods.length > 0 ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <Tabs value={activeMethod} onValueChange={(next) => setActiveMethod(next as SudoMethod)}>
                <TabsList
                  className="!grid h-10 w-full rounded-xl bg-zen-surface-muted p-1"
                  style={{ gridTemplateColumns: `repeat(${sudo.methods.length}, minmax(0, 1fr))` }}
                >
                  {sudo.methods.map((method) => {
                    const Icon = METHOD_ICON[method];
                    return (
                      <TabsTrigger
                        key={method}
                        value={method}
                        className="flex items-center gap-1.5 rounded-lg text-xs font-medium"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span>{t(METHOD_LABEL[method])}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {sudo.methods.includes('passkey-uv') ? (
                  <TabsContent value="passkey-uv" className="mt-3">
                    <p className="text-sm text-zen-text-muted">{t('auth.sudoMode.passkey.hint')}</p>
                  </TabsContent>
                ) : null}

                {sudo.methods.includes('totp') ? (
                  <TabsContent value="totp" className="mt-3 space-y-2">
                    <Label className="text-xs font-medium text-zen-text-muted">{t('auth.sudoMode.totp.label')}</Label>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(event) => setTotpCode(event.target.value)}
                      placeholder="000000"
                      disabled={submitting || isLocked}
                      className="h-11 rounded-xl"
                    />
                  </TabsContent>
                ) : null}

                {sudo.methods.includes('password') ? (
                  <TabsContent value="password" className="mt-3 space-y-2">
                    <Label className="text-xs font-medium text-zen-text-muted">
                      {t('auth.sudoMode.password.label')}
                    </Label>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={t('auth.sudoMode.password.placeholder')}
                      disabled={submitting || isLocked}
                      className="h-11 rounded-xl"
                    />
                  </TabsContent>
                ) : null}
              </Tabs>

              {errorMessage ? (
                <p className="text-sm text-red-500" role="alert">
                  {errorMessage}
                </p>
              ) : null}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
                  {t('auth.sudoMode.cancel')}
                </Button>
                <Button type="submit" disabled={submitting || isLocked || !activeMethod}>
                  {submitting ? t('auth.sudoMode.submitting') : t('auth.sudoMode.submit')}
                </Button>
              </div>
            </form>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Convenience listener-only mount — render this once at the
 * authenticated app shell to ensure SudoChallengeModal is rendered
 * for every 412 sudo-required event from any fetchWithAuth caller.
 */
export function SudoChallengeListener() {
  return <SudoChallengeModal />;
}
